import { create } from "zustand";
import { mineApi, type MineShop, type MineStatus } from "../api/mine";
import { useGameStore } from "./useGameStore";
import { useBattleStore } from "./useBattleStore";

type MineTab = "mine" | "shop";

interface MineStore {
  status: MineStatus | null;
  shop: MineShop | null;
  tab: MineTab;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  message: string | null;

  setTab: (tab: MineTab) => void;
  refresh: () => Promise<void>;
  refreshShop: () => Promise<void>;
  enter: () => Promise<void>;
  selectSlot: (slotId: number) => Promise<void>;
  claim: () => Promise<void>;
  deliver: () => Promise<void>;
  exit: () => Promise<void>;
  acceptPvp: (encounterId: string) => Promise<void>;
  buyShopItem: (code: string) => Promise<void>;
}

export const useMineStore = create<MineStore>((set, get) => ({
  status: null,
  shop: null,
  tab: "mine",
  loading: false,
  actionLoading: false,
  error: null,
  message: null,

  setTab: (tab) => set({ tab, error: null, message: null }),

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const status = await mineApi.getStatus();
      set({ status, message: status.message ?? null });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load mine" });
    } finally {
      set({ loading: false });
    }
  },

  refreshShop: async () => {
    try {
      const shop = await mineApi.getShop();
      set({ shop });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load mine shop" });
    }
  },

  enter: async () => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const status = await mineApi.enter();
      set({ status, message: status.message ?? "Entered the mine." });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Could not enter mine" });
    } finally {
      set({ actionLoading: false });
    }
  },

  selectSlot: async (slotId) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const status = await mineApi.selectSlot(slotId);
      set({ status, message: status.message ?? `Heading to point ${slotId}…` });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Could not select mining point" });
    } finally {
      set({ actionLoading: false });
    }
  },

  claim: async () => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const status = await mineApi.claim();
      set({ status, message: status.message ?? "Ore collected." });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Claim failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  deliver: async () => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const status = await mineApi.deliver();
      set({ status, message: status.message ?? "Ore delivered." });
      await useGameStore.getState().refreshBalance();
      void get().refreshShop();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Delivery failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  exit: async () => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const status = await mineApi.exit();
      set({ status, message: status.message ?? "Left the mine." });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Could not exit mine" });
    } finally {
      set({ actionLoading: false });
    }
  },

  acceptPvp: async (encounterId) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const status = await mineApi.acceptPvp(encounterId);
      set({ status, message: status.message ?? null });
      if (status.battle_id) {
        await useBattleStore.getState().loadBattle(status.battle_id);
        useGameStore.getState().setScreen("battle");
      }
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "PvP accept failed" });
    } finally {
      set({ actionLoading: false });
    }
  },

  buyShopItem: async (code) => {
    set({ actionLoading: true, error: null, message: null });
    try {
      const res = await mineApi.buyShopItem(code);
      set({ shop: res.shop, message: res.message });
      await useGameStore.getState().refreshBalance();
      void get().refresh();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Purchase failed" });
    } finally {
      set({ actionLoading: false });
    }
  },
}));
